import { useLazyQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router";

import CircleButton from "@components/buttons/CircleButton";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import JourneyTaggingFilters from "@components/forms/JourneyTaggingFilters";
import HandleQuery from "@components/HandleQuery";
import Table, { TableHeader } from "@components/Table";
import ButtonLink from "@components/typography/ButtonLink";
import InternalLink from "@components/typography/InternalLink";
import useSubscribeToAllJourneyTags from "@hooks/subscriptions/useSubscribeToAllJourneyTags";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import { TimePeriodEnum } from "@typing/Enums";
import {
  ExpertGlobalRoleEnum,
  GetJourneyTaggingsDocument,
  GetJourneyTaggingsQuery,
  GetJourneyTaggingsQueryVariables,
  JourneyTaggingSortByEnum,
  JourneyTagStatusEnum,
  JourneyUpdateDocument,
  JourneyUpdateMutation,
  JourneyUpdateMutationVariables,
  SortDirectionEnum
} from "@typing/Generated";
import { calculateDatesFromTimePeriod, getCareProfileName } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Filters = ComponentProps<typeof JourneyTaggingFilters>["filters"];

type Tagging = NonNullable<
  NonNullable<NonNullable<GetJourneyTaggingsQuery["journeyTaggings"]["edges"]>[number]>["node"]
>;

type Props = {
  tag: string;
};

const JourneyTaggingsTable = ({ tag }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const deleteOpener = useOpener();

  const [deletingTagging, setDeletingTagging] = useState<Tagging | null>(null);

  const [getJourneyTaggings, getJourneyTaggingsQuery] = useLazyQuery<
    GetJourneyTaggingsQuery,
    GetJourneyTaggingsQueryVariables
  >(GetJourneyTaggingsDocument);

  useSubscribeToAllJourneyTags(() => {
    getJourneyTaggingsQuery.refetch();
  });

  const [updateJourney] = useMutationWithValidation<JourneyUpdateMutation, JourneyUpdateMutationVariables>(
    JourneyUpdateDocument,
    "journeyUpdate",
    {
      update: () => {
        getJourneyTaggingsQuery.refetch();
      }
    }
  );

  const [filters, setFilters] = useState<Filters>({
    employerId: null,
    searchTerm: "",
    selfPaid: false,
    status: JourneyTagStatusEnum.ALL,
    timePeriod: TimePeriodEnum.ALL_TIME
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const [sortBy, setSortBy] = useState<JourneyTaggingSortByEnum>(JourneyTaggingSortByEnum.DATE_ADDED);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.DESCENDING);

  const journeyTaggings = useNodesFromConnection(getJourneyTaggingsQuery.data?.journeyTaggings);

  const tableHeaders: TableHeader<JourneyTaggingSortByEnum>[] = [
    { columnSortBy: JourneyTaggingSortByEnum.CARE_PROFILE_NAME },
    { columnSortBy: JourneyTaggingSortByEnum.OWNER_CLIENT_NAME },
    { columnSortBy: JourneyTaggingSortByEnum.OWNER_EMPLOYER_NAME },
    { columnSortBy: JourneyTaggingSortByEnum.DATE_ADDED },
    { columnSortBy: JourneyTaggingSortByEnum.STATUS },
    { columnSortBy: JourneyTaggingSortByEnum.DATE_ARCHIVED },
    { messageKey: "dictionary.actions" }
  ];

  useEffect(() => {
    if (filters.timePeriod === TimePeriodEnum.CUSTOM && (!filters.startDate || !filters.endDate)) {
      return;
    }
    const dates = calculateDatesFromTimePeriod(filters.timePeriod, filters.startDate, filters.endDate, intl);

    getJourneyTaggings({
      variables: {
        ...filters,
        endDate: dates.endDate,
        expertId: myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER ? null : myExpert?.id,
        sortBy,
        sortDirection,
        startDate: dates.startDate,
        tag
      }
    });
  }, [filters, getJourneyTaggings, intl, myExpert?.expertGlobalRole, myExpert?.id, sortBy, sortDirection, tag]);

  const generateDeleteStartHandler = useCallback(
    (tagging: Tagging) => () => {
      setDeletingTagging(tagging);
      deleteOpener.open();
    },
    [deleteOpener]
  );

  const handleDelete = useCallback(() => {
    if (deletingTagging) {
      const archivedTags = deletingTagging.journey.archivedTags.filter(tag => tag !== deletingTagging.tag);
      const tags = deletingTagging.journey.tags.filter(tag => tag !== deletingTagging.tag);
      updateJourney({ archivedTags, id: deletingTagging.journey.id, tags }).then(() => setDeletingTagging(null));
    }
  }, [deletingTagging, updateJourney]);

  return (
    <>
      <JourneyTaggingFilters filters={filters} onChange={handleFilterChange} tag={tag} />
      <HandleQuery query={getJourneyTaggingsQuery}>
        {data => (
          <Table<JourneyTaggingSortByEnum>
            fetchMore={getJourneyTaggingsQuery.fetchMore}
            headerKey="pages.admin.journeyTaggings.tableHeadings"
            headers={tableHeaders}
            pageInfo={data?.journeyTaggings.pageInfo}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            {journeyTaggings.map(journeyTagging => (
              <tr key={journeyTagging.id}>
                <td>
                  {myExpert ? (
                    <InternalLink
                      to={journeyRoute({
                        journeyId: journeyTagging.journey.id,
                        linkAfterJourney: "",
                        myExpert
                      })}
                    >
                      {getCareProfileName({ intl, journey: journeyTagging.journey })}
                    </InternalLink>
                  ) : (
                    getCareProfileName({ intl, journey: journeyTagging.journey })
                  )}
                </td>
                <td>{journeyTagging.journey.owner.fullName}</td>
                <td>
                  <ButtonLink
                    onClick={() => {
                      searchParams.set("journeyTaggingEmployerId", journeyTagging.journey.owner.employer?.id ?? "");
                      searchParams.set("journeyTaggingEmployerName", journeyTagging.journey.owner.employer?.name ?? "");
                      window.location.search = searchParams.toString();
                    }}
                  >
                    {journeyTagging.journey.owner.employer?.name}
                  </ButtonLink>
                </td>
                <td>{journeyTagging.addedAt && <DateStamp showWeekday={false} value={journeyTagging.addedAt} />}</td>
                <td>
                  {journeyTagging.archivedAt ? (
                    <FormattedMessage id="dictionary.archived" />
                  ) : (
                    <FormattedMessage id="dictionary.active" />
                  )}
                </td>
                <td>
                  {journeyTagging.archivedAt && <DateStamp showWeekday={false} value={journeyTagging.archivedAt} />}
                </td>
                <td>
                  <CircleButton
                    ariaLabelKey="accessibility.ariaLabels.delete"
                    fill="clear"
                    icon="trash"
                    onClick={generateDeleteStartHandler(journeyTagging)}
                  />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </HandleQuery>
      <DeleteConfirmationAlert
        messageKey="pages.admin.journeyTaggings.reallyDelete"
        messageValue={deletingTagging?.journey.owner.fullName}
        messageValueKey="name"
        onDelete={handleDelete}
        opener={deleteOpener}
      />
    </>
  );
};

export default JourneyTaggingsTable;
